import React from "react";
import { KgFilterPill } from "../../atoms/KgFilterPill";
import { KgNodeSearchField } from "../../atoms/KgNodeSearchField";
import { Switch } from "../../atoms/Switch";
import { KgPipelineStatus } from "../../molecules/KgPipelineStatus";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  Bar,
  PillGroup,
  RightControls,
  Row,
  RowLabel,
  SearchWrapper,
  ShowLinksLabel,
  ShowLinksWrapper,
} from "./KgCanvasFilterBar.styles";
import type {
  KgCanvasFilterBarProps,
  KgEntityFilter,
  KgGroupFilter,
} from "./KgCanvasFilterBar.types";

const ENTITY_FILTERS: { id: KgEntityFilter; label: string; dotColor?: string }[] = [
  { id: "all",        label: "All",          dotColor: rawColors.primary[500] },
  { id: "dataSource", label: "Data sources", dotColor: rawColors.accent.amber500 },
  { id: "table",      label: "Tables",       dotColor: rawColors.accent.tableBlue },
  { id: "ontology",   label: "Ontologies",   dotColor: rawColors.accent.ontologyGreen },
  { id: "metric",     label: "Metrics",      dotColor: rawColors.accent.metricBlue },
  { id: "report",     label: "Reports",      dotColor: rawColors.accent.pink },
  { id: "dataStory",  label: "Data stories", dotColor: rawColors.accent.dataStoryPurple },
  { id: "artifact",   label: "Artifacts",    dotColor: rawColors.accent.artifactBlue },
];

const GROUP_FILTERS: { id: KgGroupFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "dataObjects", label: "Data objects" },
  { id: "businessObjects", label: "Business objects" },
  { id: "outputObjects", label: "Output objects" },
];

const ShowLinksControl: React.FC<{ value: boolean; onChange: (v: boolean) => void }> = ({
  value,
  onChange,
}) => (
  <ShowLinksWrapper>
    <ShowLinksLabel>Show links</ShowLinksLabel>
    <Switch value={value} onChange={onChange} size="xs" ariaLabel="Toggle show links" />
  </ShowLinksWrapper>
);

export const KgCanvasFilterBar: React.FC<KgCanvasFilterBarProps> = ({
  variant = "canvas",
  selectedEntities,
  onEntityChange,
  selectedGroup,
  onGroupChange,
  disabledGroups,
  showLinks,
  onShowLinksChange,
  searchValue = "",
  onSearchChange,
  isFullscreen = false,
  pipelineStatus,
}) => {
  const isCatalog = variant === "catalog";

  return (
  <Bar>
    {/* Row 1 — Entity filters (multi-select). Graph build status sits top-right; in fullscreen, Show links joins it. */}
    <Row>
      <RowLabel>Entities:</RowLabel>
      <PillGroup>
        {ENTITY_FILTERS.map(({ id, label, dotColor }) => (
          <KgFilterPill
            key={id}
            label={label}
            dotColor={dotColor}
            isSelected={
              id === "all"
                ? selectedEntities.size === 0   // "All" active when nothing specific selected
                : selectedEntities.has(id)       // specific pills active when in the set
            }
            onClick={() => onEntityChange(id)}
          />
        ))}
      </PillGroup>
      {!isCatalog && (pipelineStatus || isFullscreen) && (
        <RightControls>
          {pipelineStatus && <KgPipelineStatus {...pipelineStatus} />}
          {isFullscreen && (
            <ShowLinksControl value={showLinks} onChange={onShowLinksChange} />
          )}
        </RightControls>
      )}
    </Row>

    {!isCatalog ? (
    /* Row 2 — Group filters. Normal view: Show links on right. Fullscreen: Search on right. */
    <Row>
      <RowLabel>Groups:</RowLabel>
      <PillGroup>
        {GROUP_FILTERS.map(({ id, label }) => {
          const isDisabled = id !== "all" && (disabledGroups?.has(id) ?? false);
          return (
            <KgFilterPill
              key={id}
              label={label}
              isSelected={selectedGroup === id}
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  onGroupChange(id);
                }
              }}
            />
          );
        })}
      </PillGroup>

      {!isFullscreen && (
        <RightControls>
          <ShowLinksControl value={showLinks} onChange={onShowLinksChange} />
        </RightControls>
      )}

      {isFullscreen && onSearchChange && (
        <SearchWrapper>
          <KgNodeSearchField
            value={searchValue}
            onChange={onSearchChange}
            placeholder="Search nodes"
          />
        </SearchWrapper>
      )}
    </Row>
    ) : null}
  </Bar>
  );
};

export default KgCanvasFilterBar;
